/**
 * BudgetVerdictCard — default ("Detailed") Budget Boost row.
 *
 * The same design on mobile and desktop: identity + recent form, a diverging
 * "verdict bar" that shows the rise/drop lean at a glance, the headline expected
 * price change, and an expandable panel with the per-bucket points-needed detail.
 *
 *   Mobile : stacked card (bar spans full width).
 *   Desktop: horizontal row with aligned columns and a long, readable bar.
 */

import {
  ChevronDown,
  Lock,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { useState } from "react";
import { m } from "@/paraglide/messages";
import {
  type BudgetItem,
  columnPointsNeededLabel,
  getBudgetColumns,
  isDriverCalc,
  segmentColor,
} from "../lib/budget-display";
import { BudgetResultBar, BudgetVerdictBar } from "./BudgetVerdictBar";

interface BudgetVerdictCardProps {
  item: BudgetItem;
  isPredictionMode: boolean;
  locked?: boolean;
  onLockedClick?: () => void;
  /** True when this asset is in the signed-in user's saved team. */
  inMyTeam?: boolean;
}

/**
 * Per-bucket breakdown shown when a row is expanded. Each bucket is a
 * self-contained tile — change on top, odds below it, points-needed at the
 * bottom — so the values that belong together stay together.
 */
function VerdictDetail({ item }: { item: BudgetItem }) {
  const columns = getBudgetColumns(item);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 md:px-4 pb-3 pt-2">
      {columns.map((col) => {
        const probability = item.priceChangeProbabilities[col] || 0;
        const isRise = col.startsWith("+");
        const ptsLabel = columnPointsNeededLabel(
          col,
          item.currentPrice,
          item.pointsRMinus2,
          item.pointsRMinus1,
        );
        return (
          <div
            key={col}
            className="flex flex-col gap-0.5 rounded-lg border border-border/60 bg-background/40 px-2.5 py-2 text-center"
          >
            {/* Change bucket (the heading this tile is about) */}
            <span className="flex items-center justify-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: segmentColor(col, columns) }}
                aria-hidden="true"
              />
              <span
                className={`font-mono text-xs font-semibold ${
                  isRise ? "text-success" : "text-destructive"
                }`}
              >
                {col}
              </span>
            </span>
            {/* Odds for this bucket */}
            <span className="font-mono text-lg font-bold leading-none tabular-nums">
              {probability}%
            </span>
            {/* Points needed to land in this bucket */}
            {ptsLabel && (
              <span className="font-mono text-[11px] text-muted-foreground">
                {ptsLabel} {m.f1_budget_pts_unit()}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function BudgetVerdictCard({
  item,
  isPredictionMode,
  locked = false,
  onLockedClick,
  inMyTeam = false,
}: BudgetVerdictCardProps) {
  const [expanded, setExpanded] = useState(false);

  const code = isDriverCalc(item) ? item.driverCode : item.constructorCode;
  const name = isDriverCalc(item) ? item.driverName : item.constructorName;
  const color = item.constructorColor;
  const number =
    isDriverCalc(item) && item.driverNumber !== null ? item.driverNumber : null;
  const delta = item.expectedPriceDelta;
  const deltaColor =
    delta > 0
      ? "text-success"
      : delta < 0
        ? "text-destructive"
        : "text-foreground/60";
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : null;
  const deltaText = `${delta > 0 ? "+" : ""}${delta.toFixed(2)}`;

  const ptsClass = isPredictionMode ? "text-amber-500" : "text-primary";
  const ptsPrefix = isPredictionMode ? "x" : "";

  const identity = (
    <div className="flex items-center gap-2.5 min-w-0">
      <span
        className="h-9 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-sm font-bold">{code}</span>
          {number !== null && (
            <span className="font-mono text-[11px] text-muted-foreground">
              #{number}
            </span>
          )}
          {inMyTeam && (
            <Users
              className="h-3.5 w-3.5 shrink-0 text-primary"
              aria-label={m.f1_myteam_in_team()}
            >
              <title>{m.f1_myteam_in_team()}</title>
            </Users>
          )}
        </div>
        <div className="truncate text-xs text-foreground/60">{name}</div>
      </div>
    </div>
  );

  const deltaBadge = (
    <div
      className={`flex items-center gap-1 font-mono font-bold tabular-nums ${deltaColor}`}
    >
      {DeltaIcon && <DeltaIcon className="h-3.5 w-3.5" />}
      {deltaText}
    </div>
  );

  const expandToggle = (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
    >
      {m.f1_budget_points_needed()}
      <ChevronDown
        className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
      />
    </button>
  );

  return (
    <article className="rounded-xl border border-border bg-card/40 hover:bg-card/70 transition-colors overflow-hidden">
      {/* ---------------- MOBILE ---------------- */}
      <div className="md:hidden p-3 space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          {identity}
          <div className="text-right shrink-0">
            <div className="font-mono text-sm font-semibold">
              ${item.currentPrice.toFixed(1)}M
            </div>
            {deltaBadge}
          </div>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-muted-foreground font-mono">
          <span>
            {m.f1_budget_r_minus_2()}{" "}
            {item.pointsRMinus2 !== null
              ? parseFloat(Number(item.pointsRMinus2).toFixed(2))
              : "—"}
          </span>
          <span>
            {m.f1_budget_r_minus_1()}{" "}
            {item.pointsRMinus1 !== null
              ? parseFloat(Number(item.pointsRMinus1).toFixed(2))
              : "—"}
          </span>
          <span className={ptsClass}>
            {ptsPrefix}Pts {item.expectedPoints.toFixed(1)}
          </span>
        </div>

        {locked ? (
          <LockedBar onLockedClick={onLockedClick} />
        ) : isPredictionMode ? (
          <>
            <BudgetVerdictBar item={item} />
            <div className="flex justify-end">{expandToggle}</div>
          </>
        ) : (
          <BudgetResultBar item={item} />
        )}
      </div>

      {/* ---------------- DESKTOP ---------------- */}
      <div className="hidden md:flex items-center gap-4 px-4 py-3">
        <div className="w-48 shrink-0">{identity}</div>
        <div className="w-14 shrink-0 text-right font-mono text-sm font-semibold">
          ${item.currentPrice.toFixed(1)}M
        </div>
        <div className="w-32 shrink-0 font-mono text-[11px] leading-tight text-muted-foreground">
          <div>
            {m.f1_budget_r_minus_2()}{" "}
            {item.pointsRMinus2 !== null
              ? parseFloat(Number(item.pointsRMinus2).toFixed(2))
              : "—"}{" "}
            · {m.f1_budget_r_minus_1()}{" "}
            {item.pointsRMinus1 !== null
              ? parseFloat(Number(item.pointsRMinus1).toFixed(2))
              : "—"}
          </div>
          <div className={ptsClass}>
            {ptsPrefix}Pts {item.expectedPoints.toFixed(1)}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {locked ? (
            <LockedBar onLockedClick={onLockedClick} />
          ) : isPredictionMode ? (
            <BudgetVerdictBar item={item} />
          ) : (
            <BudgetResultBar item={item} />
          )}
        </div>

        <div className="w-20 shrink-0 text-right">{deltaBadge}</div>
        <div className="w-8 shrink-0 flex justify-end">
          {!locked && isPredictionMode && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={m.f1_budget_points_needed()}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          )}
        </div>
      </div>

      {/* ---------------- SHARED EXPAND ---------------- */}
      {expanded && !locked && isPredictionMode && (
        <div className="border-t border-border/50">
          <VerdictDetail item={item} />
        </div>
      )}
    </article>
  );
}

/** Blurred placeholder bar with an unlock affordance (gated rows). */
function LockedBar({ onLockedClick }: { onLockedClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onLockedClick}
      className="relative w-full h-4 rounded-full bg-muted/40 overflow-hidden flex items-center justify-center cursor-pointer"
    >
      <div
        className="absolute inset-0 flex blur-sm opacity-40"
        aria-hidden="true"
      >
        <div className="h-full bg-destructive/40" style={{ width: "18%" }} />
        <div
          className="h-full bg-muted-foreground/25"
          style={{ width: "6%" }}
        />
        <div className="h-full bg-success/50" style={{ width: "76%" }} />
      </div>
      <span className="relative flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        <Lock className="h-3 w-3" />
        {m.f1_budget_unlock_to_reveal()}
      </span>
    </button>
  );
}
